import type { MapMouseEvent, Map as MapboxMap } from "mapbox-gl";
import type { LightningBinaryPickResult } from "./LightningBinaryPicking";

export type LightningBinaryInteractionRequest = Readonly<{
  token: number;
  point: Readonly<{ x: number; y: number }>;
  lngLat: Readonly<{ lng: number; lat: number }>;
}>;

type InteractionEntry = {
  id: string;
  registrationOrder: number;
  canPick: () => boolean;
  queuePick: (request: LightningBinaryInteractionRequest) => void;
  cancelPick: () => void;
  presentPick: (request: LightningBinaryInteractionRequest, result: LightningBinaryPickResult) => void;
};

type ActiveInteraction = {
  request: LightningBinaryInteractionRequest;
  pendingIds: Set<string>;
  results: Map<string, LightningBinaryPickResult>;
};

export type LightningBinaryInteractionHandle = Readonly<{
  report: (token: number, result: LightningBinaryPickResult) => void;
  unregister: () => void;
}>;

const coordinators = new WeakMap<MapboxMap, LightningBinaryInteractionCoordinator>();

class LightningBinaryInteractionCoordinator {
  private readonly entries = new Map<string, InteractionEntry>();
  private active?: ActiveInteraction;
  private latestToken = 0;
  private nextRegistrationOrder = 0;

  private readonly onClick = (event: MapMouseEvent) => {
    const eligible = Array.from(this.entries.values()).filter((entry) => entry.canPick());
    if (eligible.length === 0) {
      return;
    }
    this.cancelActive();
    const request: LightningBinaryInteractionRequest = {
      token: ++this.latestToken,
      point: { x: event.point.x, y: event.point.y },
      lngLat: { lng: event.lngLat.lng, lat: event.lngLat.lat },
    };
    this.active = {
      request,
      pendingIds: new Set(eligible.map((entry) => entry.id)),
      results: new Map(),
    };
    for (const entry of eligible) {
      entry.queuePick(request);
    }
  };

  private readonly onCameraStart = () => {
    this.latestToken += 1;
    this.cancelActive();
  };

  constructor(private readonly map: MapboxMap) {
    map.on("click", this.onClick);
    map.on("movestart", this.onCameraStart);
    map.on("zoomstart", this.onCameraStart);
    map.on("rotatestart", this.onCameraStart);
    map.on("pitchstart", this.onCameraStart);
  }

  register(entry: Omit<InteractionEntry, "registrationOrder">): LightningBinaryInteractionHandle {
    const registered: InteractionEntry = { ...entry, registrationOrder: this.nextRegistrationOrder++ };
    this.entries.set(entry.id, registered);
    let active = true;
    return {
      report: (token, result) => {
        if (active) {
          this.report(entry.id, token, result);
        }
      },
      unregister: () => {
        if (!active) {
          return;
        }
        active = false;
        this.unregister(entry.id);
      },
    };
  }

  private report(id: string, token: number, result: LightningBinaryPickResult): void {
    const active = this.active;
    if (!active || active.request.token !== token || !active.pendingIds.delete(id)) {
      return;
    }
    active.results.set(id, result);
    if (active.pendingIds.size === 0) {
      this.finish(active);
    }
  }

  private finish(active: ActiveInteraction): void {
    if (this.active !== active) {
      return;
    }
    this.active = undefined;
    const styleOrder = this.styleOrder();
    const chosen = Array.from(active.results.entries())
      .filter(([, result]) => result.hits.length > 0)
      .map(([id, result]) => ({ entry: this.entries.get(id), result }))
      .filter((value): value is { entry: InteractionEntry; result: LightningBinaryPickResult } => !!value.entry)
      .sort((left, right) => {
        const leftOrder = styleOrder.get(left.entry.id) ?? left.entry.registrationOrder;
        const rightOrder = styleOrder.get(right.entry.id) ?? right.entry.registrationOrder;
        return rightOrder - leftOrder;
      })[0];
    if (!chosen) {
      return;
    }
    queueMicrotask(() => {
      if (this.latestToken === active.request.token && this.entries.get(chosen.entry.id) === chosen.entry) {
        chosen.entry.presentPick(active.request, chosen.result);
      }
    });
  }

  private styleOrder(): ReadonlyMap<string, number> {
    try {
      return new Map(this.map.getStyle().layers.map((layer, index) => [layer.id, index]));
    } catch (_error) {
      return new Map();
    }
  }

  private cancelActive(): void {
    if (!this.active) {
      return;
    }
    this.active = undefined;
    for (const entry of this.entries.values()) {
      entry.cancelPick();
    }
  }

  private unregister(id: string): void {
    const entry = this.entries.get(id);
    if (!entry) {
      return;
    }
    entry.cancelPick();
    this.entries.delete(id);
    if (this.active?.pendingIds.delete(id) && this.active.pendingIds.size === 0) {
      this.finish(this.active);
    }
    if (this.entries.size === 0) {
      this.dispose();
      coordinators.delete(this.map);
    }
  }

  private dispose(): void {
    this.latestToken += 1;
    this.cancelActive();
    this.map.off("click", this.onClick);
    this.map.off("movestart", this.onCameraStart);
    this.map.off("zoomstart", this.onCameraStart);
    this.map.off("rotatestart", this.onCameraStart);
    this.map.off("pitchstart", this.onCameraStart);
  }
}

export function registerLightningBinaryInteraction(
  map: MapboxMap,
  entry: Omit<InteractionEntry, "registrationOrder">,
): LightningBinaryInteractionHandle {
  let coordinator = coordinators.get(map);
  if (!coordinator) {
    coordinator = new LightningBinaryInteractionCoordinator(map);
    coordinators.set(map, coordinator);
  }
  return coordinator.register(entry);
}
